// GaugePicker — single source of customization for which gauges appear in
// the Orchestrator inbox strip.
//
// The picker shows every gauge in the registry (defaults included) so any
// user can deselect a default they don't care about and pin only what they
// work with. The user's pick is stored in localStorage under a role-scoped
// key, so a buyer and an engineer don't share state.
//
// Behavior:
//   - Initial state ⇒ role defaults (registry-driven)
//   - First toggle ⇒ snapshot defaults into the user's selection, then apply
//   - Reset ⇒ clear the saved selection and fall back to role defaults
//
// Visual: n×m palette of icon+label tiles, selected tiles tinted in the
// gauge's color.

import React, { useState } from 'react';
import {
  Box, IconButton, Tooltip, Popover, Typography,
  Button, Divider, alpha, ButtonBase,
} from '@mui/material';
import { Add as AddIcon, CheckCircle as CheckIcon } from '@mui/icons-material';

import {
  GAUGE_REGISTRY,
  ALL_GAUGE_IDS,
  ROLE_DEFAULT_GAUGES,
  saveUserGaugeSelection,
} from './gauges-registry';

interface GaugePickerProps {
  role: string;
  /** Currently effective selection (defaults or user-pinned). The picker
   *  treats every id in this array as "currently visible." */
  selection: string[];
  /** Apply a new explicit selection (and persist). Use `null` to reset to
   *  role defaults. */
  onChange: (next: string[] | null) => void;
}

const GaugePicker: React.FC<GaugePickerProps> = ({ role, selection, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const selectionSet = new Set(selection);

  const toggle = (id: string) => {
    const next = selection.includes(id)
      ? selection.filter((x) => x !== id)
      : [...selection, id];
    saveUserGaugeSelection(role, next);
    onChange(next);
  };

  const resetToDefaults = () => {
    if (typeof window !== 'undefined') {
      try { localStorage.removeItem(`orchestrator.gauges.${role}`); } catch { /* noop */ }
    }
    onChange(null);
  };

  const isDefault = (id: string) =>
    (ROLE_DEFAULT_GAUGES[role] ?? []).includes(id);

  return (
    <>
      <Tooltip title="Customize gauges" arrow>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            width: 36,
            height: 36,
            border: '1px dashed',
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': { bgcolor: (t) => alpha(t.palette.text.primary, 0.04) },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: { sx: { p: 2, borderRadius: 2.5, width: 360 } },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.25 }}>
          Customize gauges
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', mb: 1.5 }}>
          Tap a tile to add or remove it from your strip.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1.25,
          }}
        >
          {ALL_GAUGE_IDS.map((id) => {
            const def = GAUGE_REGISTRY[id];
            if (!def) return null;
            const isOn = selectionSet.has(id);
            return (
              <Tooltip
                key={id}
                title={def.description}
                placement="top"
                arrow
                enterDelay={400}
              >
              <ButtonBase
                onClick={() => toggle(id)}
                focusRipple
                aria-pressed={isOn}
                aria-label={`${isOn ? 'Remove' : 'Add'} ${def.label} gauge`}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  py: 1.5,
                  px: 1,
                  borderRadius: 2,
                  border: '1.5px solid',
                  borderColor: isOn ? def.color : alpha(def.color, 0.18),
                  bgcolor: isOn ? alpha(def.color, 0.10) : 'transparent',
                  transition: 'all 140ms cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  minHeight: 92,
                  '&:hover': {
                    bgcolor: isOn ? alpha(def.color, 0.14) : alpha(def.color, 0.05),
                    borderColor: isOn ? def.color : alpha(def.color, 0.45),
                    transform: 'translateY(-1px)',
                  },
                  '&:active': { transform: 'translateY(0)' },
                }}
              >
                {isOn && (
                  <CheckIcon sx={{
                    position: 'absolute', top: 6, right: 6,
                    fontSize: 16, color: def.color,
                  }} />
                )}
                {/* Subtle "default" hint under non-selected default tiles —
                    so the user knows what would come back on Reset. */}
                {!isOn && isDefault(id) && (
                  <Typography sx={{
                    position: 'absolute', top: 4, right: 6,
                    fontSize: '0.6rem', color: 'text.disabled', fontWeight: 600,
                  }}>
                    default
                  </Typography>
                )}
                <def.Icon sx={{ fontSize: 26, color: def.color }} />
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: isOn ? 700 : 600,
                    color: isOn ? def.color : 'text.primary',
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                  }}
                >
                  {def.label}
                </Typography>
              </ButtonBase>
              </Tooltip>
            );
          })}
        </Box>

        <Divider sx={{ mt: 1.75, mb: 0.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {selectionSet.size} shown
          </Typography>
          <Box>
            <Button size="small" onClick={resetToDefaults}>
              Reset to defaults
            </Button>
            <Button size="small" onClick={() => setAnchorEl(null)}>
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default GaugePicker;
